// @ts-check

import { api } from '@/api'

/**
 * @returns {Promise<Turnover>}
 */
export const getTurnoverOptions = async () => {
  const res = await api.get('annual/sales')
  return res.data
}

/**
 * @param {BankAccountResource['id']} bankAccountId
 * @returns {Promise<{ date: string, balance: import('@spl-company-oy/core/lib/src/currency/types').Money}>}
 */
export const getAccountBalance = async (bankAccountId) => {
  const res = await api.get(`bank-accounts/${bankAccountId}/account-balance`)
  return res.data
}

export const getNotifications = async () => api.get('notifications')

/**
 * @param {CompanyResource['id']} companyId
 * @param {TurnoverResource['id']} turnoverEstimate
 * @returns {Promise<CompanyResource>}
 */
export const updateTurnoverEstimate = async (companyId, turnoverEstimate) => {
  const res = await api.put(`company/${companyId}/turnover-estimate`, {
    turnover_estimate: turnoverEstimate
  })
  return res.data
}

/**
 * @param {{ language?: string }} params
 * @returns {Promise<CompanyIndustryResource>}
 */
export const getCompanyIndustry = async (params = {}) => {
  const res = await api.get('company/industry', {
    params
  })
  return res.data
}

/**
 * @returns {Promise<Record<string, any>>}
 */
export const getCompanySettings = async () => {
  const res = await api.get('company/settings')
  return res.data
}

/**
 * @returns {Promise<Features>}
 */
export const getFeatures = async () => {
  const res = await api.get('features')
  return res.data
}

/**
 * @param {CompanyResource['id']} companyId
 * @param {CompanyUpdatePayload} payload
 * @returns {Promise<CompanyResource>}
 */
export const updateCompany = async (companyId, payload) => {
  const res = await api.patch(`company/${companyId}`, payload)
  return res.data
}

/**
 * @param {CompanyResource['id']} companyId
 * @param {CompanyIndustryUpdatePayload} payload
 * @returns {Promise<CompanyResource>}
 */
export const updateCompanyIndustry = async (companyId, payload) => {
  const res = await api.put(`company/${companyId}/industry`, payload)
  return res.data
}

/**
 * @returns {Promise<YtjRegistriesResource>}
 */
export const getYtjRegistries = async () => {
  const res = await api.get('ytj-registries')
  return res.data
}

/**
 * Search companies by name or business id using backend company register integration.
 * This endpoint should return a list of candidate companies with minimal details used by the form.
 * @param {{ q: string }} params
 * @returns {Promise<Array<Record<string, any>>>}
 */
export const searchCompanies = async (params = {}) => {
  // Thin wrapper: call backend with provided params and return raw response data.
  // Any response shape normalization should be handled by the caller.
  const res = await api.get('companies/search', { params })
  return res.data
}

/**
 * Fetch full company details by business id.
 * @param {string} businessId
 * @returns {Promise<Record<string, any>>}
 */
export const getCompanyByBusinessId = async (businessId) => {
  const res = await api.get(`companies/${businessId}`)
  return res.data
}
